// symbolSeparationService.ts - Separate SPY and SPX data properly
import { ScannerData } from '../types/ScannerData';

export interface SeparatedData {
  SPY: ScannerData[];
  SPX: ScannerData[];
  QQQ: ScannerData[];
  GLD: ScannerData[];
  Other: ScannerData[];
}

export class SymbolSeparationService {
  // Separate data by underlying symbol
  static separateByUnderlying(data: ScannerData[]): SeparatedData {
    const separated: SeparatedData = {
      SPY: [],
      SPX: [],
      QQQ: [],
      GLD: [],
      Other: []
    };

    data.forEach(record => {
      const underlying = record.Underlying?.toUpperCase();
      
      switch (underlying) {
        case 'SPY':
          separated.SPY.push(record);
          break;
        case '$SPXW.X':
        case 'SPX':
          separated.SPX.push(record);
          break;
        case 'QQQ':
          separated.QQQ.push(record);
          break;
        case 'GLD':
          separated.GLD.push(record);
          break;
        default:
          separated.Other.push(record);
      }
    });

    return separated;
  }

  // Get strike ranges for a specific underlying
  static getStrikeRanges(data: ScannerData[], underlying: string): { min: number; max: number; strikes: number[] } {
    const filteredData = data.filter(record => 
      record.Underlying?.toUpperCase() === underlying.toUpperCase()
    );

    if (filteredData.length === 0) {
      return { min: 0, max: 0, strikes: [] };
    }

    const strikes = filteredData
      .map(record => record.Strike)
      .filter(strike => strike > 0)
      .sort((a, b) => a - b);

    return {
      min: Math.min(...strikes),
      max: Math.max(...strikes),
      strikes
    };
  }

  // Get price ranges for indicator pane
  static getPriceRanges(separatedData: SeparatedData): Record<string, { min: number; max: number; strikes: number[] }> {
    const ranges: Record<string, { min: number; max: number; strikes: number[] }> = {};

    Object.keys(separatedData).forEach(symbol => {
      const data = separatedData[symbol as keyof SeparatedData];
      ranges[symbol] = this.getStrikeRanges(data, symbol);
    });

    return ranges;
  }

  // Filter data for specific underlying
  static filterByUnderlying(data: ScannerData[], underlying: string): ScannerData[] {
    return data.filter(record => 
      record.Underlying?.toUpperCase() === underlying.toUpperCase()
    );
  }

  // Get all unique underlyings
  static getUniqueUnderlyings(data: ScannerData[]): string[] {
    const underlyings = new Set<string>();
    
    data.forEach(record => {
      if (record.Underlying) {
        underlyings.add(record.Underlying.toUpperCase());
      }
    });

    return Array.from(underlyings).sort();
  }

  // Validate symbol separation
  static validateSeparation(data: ScannerData[], separatedData: SeparatedData): boolean {
    const totalOriginal = data.length;
    const totalSeparated = Object.values(separatedData).reduce((sum, arr) => sum + arr.length, 0);
    
    return totalOriginal === totalSeparated;
  }

  // Get summary statistics for each underlying
  static getSummaryStats(separatedData: SeparatedData): Record<string, {
    count: number;
    callCount: number;
    putCount: number;
    totalVolume: number;
    totalDelta: number;
    strikeRange: { min: number; max: number };
  }> {
    const stats: Record<string, any> = {};

    Object.keys(separatedData).forEach(symbol => {
      const data = separatedData[symbol as keyof SeparatedData];
      
      if (data.length === 0) {
        stats[symbol] = {
          count: 0,
          callCount: 0,
          putCount: 0,
          totalVolume: 0,
          totalDelta: 0,
          strikeRange: { min: 0, max: 0 }
        };
        return;
      }

      const calls = data.filter(record => record['Option Type'] === 'C');
      const puts = data.filter(record => record['Option Type'] === 'P');
      
      const totalVolume = data.reduce((sum, record) => sum + (record.Volume || 0), 0);
      const totalDelta = data.reduce((sum, record) => sum + (record['Delta ( ask - bid )'] || 0), 0);
      
      const strikes = data.map(record => record.Strike).filter(s => s > 0);
      const strikeRange = strikes.length > 0 ? {
        min: Math.min(...strikes),
        max: Math.max(...strikes)
      } : { min: 0, max: 0 };

      stats[symbol] = {
        count: data.length,
        callCount: calls.length,
        putCount: puts.length,
        totalVolume,
        totalDelta,
        strikeRange
      };
    });

    return stats;
  }
}

export default SymbolSeparationService;
